import {
  IconDotsDiagonal,
  IconGrain,
  IconGridDots,
  IconPoint,
} from '@tabler/icons-react';
import { AnticipatedCareLevel } from '@zorro/clients';
import { Alert } from '@zorro/design-system/v2/Alert';
import { RadioGroup } from '@zorro/design-system/v2/Radio';
import { RadioCardBox } from '@zorro/design-system/v2/RadioCardBox';
import { Grid, Stack, Text } from '@zorro/web/design-system';
import { ComponentType, ForwardedRef, forwardRef } from 'react';
import { ControllerRenderProps } from 'react-hook-form';

type TablerIcon = ComponentType<{ size?: number; stroke?: number }>;

type CareLevelOption = {
  value: AnticipatedCareLevel;
  title: string;
  Icon: TablerIcon;
  bulletPoints: string[];
};

const anticipatedCareLevelOptions: readonly CareLevelOption[] = [
  {
    value: AnticipatedCareLevel.ESSENTIAL,
    title: 'Essential',
    Icon: IconPoint,
    bulletPoints: [
      '1-2 routine visits',
      'No regular medications',
      'No hospital stays',
    ],
  },
  {
    value: AnticipatedCareLevel.MODERATE,
    title: 'Moderate',
    Icon: IconDotsDiagonal,
    bulletPoints: [
      '3-4 doctor visits',
      '1-2 regular medications',
      'Minor planned care',
    ],
  },
  {
    value: AnticipatedCareLevel.ABOVE_AVERAGE,
    title: 'Above average',
    Icon: IconGrain,
    bulletPoints: [
      '5-8 medical visits',
      '3-4 regular medications',
      'Planned hospital care',
    ],
  },
  {
    value: AnticipatedCareLevel.EXTENSIVE,
    title: 'Extensive',
    Icon: IconGridDots,
    bulletPoints: [
      '9+ visits or specialists',
      '5+ or specialty meds',
      'Major surgical care',
    ],
  },
];

type Props = Omit<ControllerRenderProps, 'ref' | 'value'> & {
  value?: AnticipatedCareLevel | null;
  isRequired?: boolean;
};

export const AnticipatedCareLevelInput = forwardRef(
  ({ isRequired, ...field }: Props, ref: ForwardedRef<HTMLDivElement>) => {
    return (
      <Stack gap="var(--space-4)">
        <Text fw={600}>Care needs{isRequired ? '*' : ''}</Text>
        <Text size="sm" c="dimmed" mb="var(--space-4)">
          What level of care do you anticipate needing this year?
        </Text>
        <Alert variant="general-note" mb="var(--space-20)">
          Your selected care level is only used to estimate your expected
          healthcare needs. It doesn&apos;t limit how often you can see a doctor
          or the care your plan covers.
        </Alert>
        <RadioGroup {...field} ref={ref}>
          <Grid>
            {anticipatedCareLevelOptions.map(
              ({ value: optionValue, title, Icon, bulletPoints }) => (
                <Grid.Col key={optionValue} span={{ base: 6, md: 3 }}>
                  <RadioCardBox
                    value={optionValue}
                    title={title}
                    icon={<Icon size={16} stroke={1.25} />}
                    iconColor="indigo"
                    bulletPoints={bulletPoints}
                    h="100%"
                  />
                </Grid.Col>
              )
            )}
          </Grid>
        </RadioGroup>
      </Stack>
    );
  }
);

AnticipatedCareLevelInput.displayName = 'AnticipatedCareLevelInput';
